import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get, post, put } from "aws-amplify/api";
import { Alert, Button, Card, Col, Form, Nav, Row } from "react-bootstrap";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createElementIdFactory, createSLMOwnerDetailElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/Detail.css";
import "containers/slm/lease/SLMEntityDetail.css";

const defaultOwner = {
  owner_org_id: null,
  sale_id: null,
  lease_id: null,
  name: "",
  name_key: "",
  mail_street: "",
  mail_state: null,
  mail_suburb: "",
  mail_code: "",
  phys_street: "",
  phys_state: null,
  phys_suburb: "",
  phys_code: "",
  phone_ext: "",
  phone_no: "",
  fax_ext: "",
  fax_no: "",
  email: "",
  web: "",
  notes: "",
  created_date: null,
  created_by: null,
  last_edited_date: null,
  last_edited_by: null,
};

const parseResponseJson = async (operation) => {
  const response = await operation.response;
  return response.body.json();
};

const normalizeStateOption = (option) => {
  if (!option) {
    return null;
  }

  const value = option.value ?? option.state_code ?? option.code ?? null;
  const label = option.label ?? option.state_name ?? option.name ?? value;

  if (value === null || label === null) {
    return null;
  }

  return {
    value: String(value),
    label: String(label),
  };
};

const toStateOptionByValue = (stateValue, stateOptions) => {
  if (!stateValue) {
    return null;
  }

  return stateOptions.find((opt) => String(opt.value) === String(stateValue)) || {
    value: String(stateValue),
    label: String(stateValue),
  };
};

function SLMOwnerDetail({ isAuthenticated, user }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const createId = useMemo(() => createElementIdFactory("slm-owner-detail"), []);
  const elementIds = useMemo(() => createSLMOwnerDetailElementIds(createId), [createId]);
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const [isLoading, setIsLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isOwnerDirty, setIsOwnerDirty] = useState(false);
  const [owner, setOwner] = useState(defaultOwner);
  const [ownerContacts, setOwnerContacts] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedPhysState, setSelectedPhysState] = useState(null);
  const [selectedMailState, setSelectedMailState] = useState(null);
  const [idList, setIdList] = useState([]);
  const [currentIdIndex, setCurrentIdIndex] = useState(null);

  const isNewOwner = routeId === "new";
  const parsedOwnerId = Number(routeId);
  const hasExistingOwnerId = Number.isFinite(parsedOwnerId) && parsedOwnerId > 0;
  const previousId = currentIdIndex !== null && currentIdIndex > 0 ? idList[currentIdIndex - 1] : null;
  const nextId = currentIdIndex !== null && currentIdIndex + 1 < idList.length ? idList[currentIdIndex + 1] : null;
  const currentOwnerId = owner.owner_org_id ?? (hasExistingOwnerId ? parsedOwnerId : null);

  const setOwnerField = useCallback((field, value) => {
    setOwner((previous) => ({
      ...previous,
      [field]: value,
    }));
    setIsOwnerDirty(true);
  }, []);

  const sanitizePostcode = useCallback((rawValue) => String(rawValue || "").replace(/\D/g, "").slice(0, 4), []);

  const hydrateBrowseState = useCallback((incomingIdList, currentId) => {
    const safeList = Array.isArray(incomingIdList)
      ? incomingIdList.map((value) => Number(value)).filter((value) => Number.isFinite(value))
      : [];

    if (safeList.length === 0 || !Number.isFinite(currentId)) {
      setIdList([]);
      setCurrentIdIndex(null);
      return;
    }

    const resolvedIndex = safeList.indexOf(currentId);
    setIdList(safeList);
    setCurrentIdIndex(resolvedIndex >= 0 ? resolvedIndex : null);
  }, []);

  const loadStates = useCallback(async () => {
    try {
      const statesResponse = await parseResponseJson(
        get({ apiName: "colin", path: "/slmstates", options: { queryParams: {} } })
      );

      const normalizedStates = (Array.isArray(statesResponse) ? statesResponse : [])
        .map((option) => normalizeStateOption(option))
        .filter(Boolean);

      setStateOptions(normalizedStates);
      return normalizedStates;
    } catch (error) {
      console.error("Failed to load SLM states", error);
      setStateOptions([]);
      return [];
    }
  }, []);

  const getOwnerContacts = useCallback(async (ownerOrgId) => {
    if (!ownerOrgId) {
      setOwnerContacts([]);
      return;
    }

    try {
      const contacts = await parseResponseJson(
        get({
          apiName: "colin",
          path: `/slmowners/${ownerOrgId}/slmcontacts`,
          options: { queryParams: { orgid: ownerOrgId } },
        })
      );
      setOwnerContacts(Array.isArray(contacts) ? contacts : []);
    } catch (error) {
      console.error("Failed to load owner contacts", error);
      setOwnerContacts([]);
    }
  }, []);

  const loadOwner = useCallback(async (ownerId, availableStates) => {
    const loadedOwner = await parseResponseJson(
      get({
        apiName: "colin",
        path: `/slmowners/${ownerId}`,
        options: { queryParams: {} },
      })
    );

    const normalizedOwner = {
      ...defaultOwner,
      ...loadedOwner,
    };

    setOwner(normalizedOwner);
    setSelectedPhysState(toStateOptionByValue(normalizedOwner.phys_state, availableStates));
    setSelectedMailState(toStateOptionByValue(normalizedOwner.mail_state, availableStates));

    await getOwnerContacts(normalizedOwner.owner_org_id);
    document.title = normalizedOwner.name || "SLM Owner";
  }, [getOwnerContacts]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let isMounted = true;

    (async () => {
      setIsLoading(true);
      setShowAlert(false);
      setErrorMessage("");

      try {
        const availableStates = await loadStates();

        if (!isMounted) {
          return;
        }

        if (isNewOwner) {
          setOwner(defaultOwner);
          setSelectedPhysState(null);
          setSelectedMailState(null);
          setOwnerContacts([]);
          document.title = "New owner record";
        } else if (hasExistingOwnerId) {
          await loadOwner(parsedOwnerId, availableStates);
          hydrateBrowseState(location?.state?.data, parsedOwnerId);
        } else {
          setOwner(defaultOwner);
          setSelectedPhysState(null);
          setSelectedMailState(null);
          setOwnerContacts([]);
          setIdList([]);
          setCurrentIdIndex(null);
          document.title = "Owner Details";
        }
      } catch (error) {
        console.error("Failed to load owner", error);
        if (isMounted) {
          setShowAlert(true);
          setErrorMessage("Error loading owner details. Please refresh and try again.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [hasExistingOwnerId, hydrateBrowseState, isAuthenticated, isNewOwner, loadOwner, loadStates, location?.state?.data, parsedOwnerId]);

  const validateOwner = useCallback(() => {
    if (!owner.name || !String(owner.name).trim()) {
      return "You must enter a name.";
    }

    return "";
  }, [owner.name]);

  const saveOwner = useCallback(async () => {
    const payload = {
      ...owner,
      last_edited_by: user,
    };

    if (isNewOwner || !payload.owner_org_id) {
      payload.created_by = user;
      return parseResponseJson(
        post({
          apiName: "colin",
          path: "/slmowners/new",
          options: { body: payload },
        })
      );
    }

    return parseResponseJson(
      put({
        apiName: "colin",
        path: `/slmowners/${payload.owner_org_id}`,
        options: { body: payload },
      })
    );
  }, [isNewOwner, owner, user]);

  const handleOwnerBlur = useCallback(async () => {
    if (!isOwnerDirty) {
      return;
    }

    const validationMessage = validateOwner();
    if (validationMessage) {
      setShowAlert(true);
      setErrorMessage(validationMessage);
      return;
    }

    setShowAlert(false);
    setErrorMessage("");

    try {
      setIsOwnerDirty(false);
      const result = await saveOwner();

      if (result?.result === false) {
        setShowAlert(true);
        setErrorMessage(result.error_message || "Failed to save owner.");
        return;
      }

      if ((isNewOwner || !owner.owner_org_id) && result?.id) {
        navigate(`/slmowners/${result.id}`);
      }
    } catch (error) {
      console.error("Failed to save owner", error);
      setShowAlert(true);
      setErrorMessage("Failed to save owner.");
    }
  }, [isNewOwner, isOwnerDirty, navigate, owner.owner_org_id, saveOwner, validateOwner]);

  const updateContactTableState = useCallback((rowIndex, columnId, value) => {
    setOwnerContacts((previous) =>
      previous.map((record, index) => {
        if (index !== rowIndex) {
          return record;
        }

        return {
          ...record,
          [columnId]: value,
          modified: true,
        };
      })
    );
  }, []);

  const saveContactRecord = useCallback(async ({ row, column, value }) => {
    const rowIndex = row?.index;
    if (!Number.isInteger(rowIndex) || rowIndex < 0) {
      return false;
    }

    const sourceRecord = ownerContacts[rowIndex];
    if (!sourceRecord) {
      return false;
    }

    const ownerOrgId = currentOwnerId;
    if (!ownerOrgId) {
      setShowAlert(true);
      setErrorMessage("Save owner before adding contacts.");
      return false;
    }

    const nextRecord = {
      ...sourceRecord,
      [column.id]: value,
      entity_org_id: ownerOrgId,
      owner_org_id: ownerOrgId,
      last_edited_by: user,
    };

    const isNew = Boolean(nextRecord.new_record || !nextRecord.owner_person_id);
    const path = isNew
      ? `/slmowners/${ownerOrgId}/slmcontacts/new`
      : `/slmowners/${ownerOrgId}/slmcontacts/${nextRecord.owner_person_id}`;

    try {
      const operation = isNew
        ? post({ apiName: "colin", path, options: { body: nextRecord } })
        : put({ apiName: "colin", path, options: { body: nextRecord } });

      const result = await parseResponseJson(operation);

      if (result?.result === false) {
        setShowAlert(true);
        setErrorMessage(result?.error_message || "Unable to save contact.");
        return false;
      }

      setOwnerContacts((previous) =>
        previous.map((record, index) => {
          if (index !== rowIndex) {
            return record;
          }

          return {
            ...record,
            ...nextRecord,
            owner_person_id: result?.id ?? record.owner_person_id,
            new_record: false,
            modified: false,
          };
        })
      );

      setShowAlert(false);
      setErrorMessage("");
      return true;
    } catch (error) {
      console.error("Failed to save owner contact", error);
      setShowAlert(true);
      setErrorMessage("Failed to save owner contact.");
      return false;
    }
  }, [currentOwnerId, ownerContacts, user]);

  const addContact = useCallback(() => {
    if (!currentOwnerId) {
      setShowAlert(true);
      setErrorMessage("Save owner before adding contacts.");
      return;
    }

    setOwnerContacts((previous) => [
      {
        owner_person_id: null,
        entity_org_id: currentOwnerId,
        owner_org_id: currentOwnerId,
        title: "",
        first_name: "",
        last_name: "",
        position: "",
        phone_ext: "",
        phone_no: "",
        email: "",
        notes: "",
        created_by: user,
        last_edited_by: user,
        new_record: true,
        modified: false,
      },
      ...previous,
    ]);
  }, [currentOwnerId, user]);

  const ownerContactsColumns = useMemo(() => {
    const asDisplay = (value) => (value === null || value === undefined ? "" : value);

    return [
      columnHelper.accessor("owner_person_id", {
        id: "owner_person_id",
        header: "Contact ID",
        cell: (info) => asDisplay(info.getValue()),
        size: 110,
      }),
      columnHelper.accessor("title", {
        id: "title",
        header: "Title",
        cell: EditableCell,
        size: 110,
      }),
      columnHelper.accessor("first_name", {
        id: "first_name",
        header: "First Name",
        cell: EditableCell,
        size: 140,
      }),
      columnHelper.accessor("last_name", {
        id: "last_name",
        header: "Last Name",
        cell: EditableCell,
        size: 140,
      }),
      columnHelper.accessor("position", {
        id: "position",
        header: "Position",
        cell: EditableCell,
        size: 130,
      }),
      columnHelper.accessor("phone_ext", {
        id: "phone_ext",
        header: "Phone Ext.",
        cell: EditableCell,
        size: 120,
      }),
      columnHelper.accessor("phone_no", {
        id: "phone_no",
        header: "Phone No.",
        cell: EditableCell,
        size: 130,
      }),
      columnHelper.accessor("email", {
        id: "email",
        header: "Email",
        cell: EditableCell,
        size: 190,
      }),
      columnHelper.accessor("notes", {
        id: "notes",
        header: "Notes",
        cell: EditableCell,
        size: 180,
      }),
    ];
  }, [columnHelper]);

  const handleBrowseToRecord = useCallback((recordId) => {
    if (!recordId) {
      return;
    }

    navigate(`/slmowners/${recordId}`, { state: { data: idList } });
  }, [idList, navigate]);

  const handleCurrentIndexChange = useCallback((event) => {
    const nextValue = event.target.value.replace(/[^0-9]/g, "");

    if (!nextValue) {
      setCurrentIdIndex(null);
      return;
    }

    const requestedIndex = Number(nextValue) - 1;
    if (Number.isFinite(requestedIndex) && requestedIndex >= 0 && requestedIndex < idList.length) {
      setCurrentIdIndex(requestedIndex);
    }
  }, [idList.length]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="Entity slm-entity-detail" id={elementIds.root}>
      {isLoading && (
        <div className="outer-div">
          <div className="sweet-loading inner-div">
            <PacmanLoader cssOverride={{ display: "block", margin: "0 auto" }} size={20} color="#e03c31" loading={isLoading} />
          </div>
        </div>
      )}

      {!isLoading && (
        <Form id={elementIds.form} className="slm-entity-detail-form">
          <Alert show={showAlert} variant="danger" onClose={() => setShowAlert(false)} dismissible>
            {errorMessage}
          </Alert>

          <Row className="align-items-center g-2 mb-2">
            <Col xs={12} lg={4}>
              <h6 id={elementIds.title} className="mb-0">
                {owner.name ? `Owner Details - ${owner.name}` : "Owner Details"}
              </h6>
            </Col>
            <Col xs={12} lg={4}>
              <Nav className="justify-content-lg-center flex-wrap gap-2">
                {previousId !== null && (
                  <Button id={elementIds.previousButton} type="button" variant="info" size="sm" onClick={() => handleBrowseToRecord(previousId)}>
                    Previous
                  </Button>
                )}
                {nextId !== null && (
                  <Button id={elementIds.nextButton} type="button" variant="info" size="sm" onClick={() => handleBrowseToRecord(nextId)}>
                    Next
                  </Button>
                )}
                {idList.length > 0 && (
                  <Form.Control
                    id={elementIds.browseIndexInput}
                    value={currentIdIndex === null ? "" : currentIdIndex + 1}
                    onChange={handleCurrentIndexChange}
                    onBlur={() => handleBrowseToRecord(idList[currentIdIndex])}
                    type="text"
                    className="slm-entity-detail-page-input"
                    aria-label="Current record index"
                  />
                )}
              </Nav>
            </Col>
            <Col xs={12} lg={4} className="d-flex justify-content-lg-end" />
          </Row>

          <Row className="g-3 slm-entity-detail-section">
            <Col xs={12} md={6} xl={3}>
              <Card className="slm-detail-card h-100">
                <Card.Header>Owner Details</Card.Header>
                <Card.Body>
                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="owner_org_id">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Owner ID</Form.Label>
                    <Col sm={8}>
                      <Form.Control value={owner.owner_org_id || ""} type="text" disabled readOnly />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="name">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Name</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.nameInput}
                        value={owner.name || ""}
                        onChange={(event) => setOwnerField("name", event.target.value)}
                        onBlur={handleOwnerBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="phone_ext">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Telephone</Form.Label>
                    <Col sm={4}>
                      <Form.Control
                        id={elementIds.phoneExtInput}
                        value={owner.phone_ext || ""}
                        onChange={(event) => setOwnerField("phone_ext", event.target.value)}
                        onBlur={handleOwnerBlur}
                        type="text"
                      />
                    </Col>
                    <Col sm={4}>
                      <Form.Control
                        id={elementIds.phoneNoInput}
                        value={owner.phone_no || ""}
                        onChange={(event) => setOwnerField("phone_no", event.target.value)}
                        onBlur={handleOwnerBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="fax_ext">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Fax</Form.Label>
                    <Col sm={4}>
                      <Form.Control
                        id={elementIds.faxExtInput}
                        value={owner.fax_ext || ""}
                        onChange={(event) => setOwnerField("fax_ext", event.target.value)}
                        onBlur={handleOwnerBlur}
                        type="text"
                      />
                    </Col>
                    <Col sm={4}>
                      <Form.Control
                        id={elementIds.faxNoInput}
                        value={owner.fax_no || ""}
                        onChange={(event) => setOwnerField("fax_no", event.target.value)}
                        onBlur={handleOwnerBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-0 align-items-center" controlId="email">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Email</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.emailInput}
                        value={owner.email || ""}
                        onChange={(event) => setOwnerField("email", event.target.value.trimStart())}
                        onBlur={handleOwnerBlur}
                        type="email"
                      />
                    </Col>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6} xl={3}>
              <Card className="slm-detail-card h-100">
                <Card.Header>Street Address</Card.Header>
                <Card.Body>
                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="phys_street">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Street Address</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.physStreetInput}
                        value={owner.phys_street || ""}
                        onChange={(event) => setOwnerField("phys_street", event.target.value)}
                        onBlur={handleOwnerBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="phys_suburb">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Suburb</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.physSuburbInput}
                        value={owner.phys_suburb || ""}
                        onChange={(event) => setOwnerField("phys_suburb", event.target.value)}
                        onBlur={handleOwnerBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="phys_state">
                    <Form.Label column sm={4} className="text-sm-end mb-0">State</Form.Label>
                    <Col sm={8}>
                      <Select
                        inputId={elementIds.physStateSelect}
                        value={selectedPhysState}
                        onChange={(selection) => {
                          setSelectedPhysState(selection);
                          setOwnerField("phys_state", selection?.value || null);
                        }}
                        onBlur={handleOwnerBlur}
                        options={stateOptions}
                        isClearable
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-0 align-items-center" controlId="phys_code">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Postcode</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.physCodeInput}
                        value={owner.phys_code || ""}
                        onChange={(event) => setOwnerField("phys_code", sanitizePostcode(event.target.value))}
                        onBlur={handleOwnerBlur}
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                      />
                    </Col>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6} xl={3}>
              <Card className="slm-detail-card h-100">
                <Card.Header>Postal Address</Card.Header>
                <Card.Body>
                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="mail_street">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Postal Address</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.mailStreetInput}
                        value={owner.mail_street || ""}
                        onChange={(event) => setOwnerField("mail_street", event.target.value)}
                        onBlur={handleOwnerBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="mail_suburb">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Suburb</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.mailSuburbInput}
                        value={owner.mail_suburb || ""}
                        onChange={(event) => setOwnerField("mail_suburb", event.target.value)}
                        onBlur={handleOwnerBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="mail_state">
                    <Form.Label column sm={4} className="text-sm-end mb-0">State</Form.Label>
                    <Col sm={8}>
                      <Select
                        inputId={elementIds.mailStateSelect}
                        value={selectedMailState}
                        onChange={(selection) => {
                          setSelectedMailState(selection);
                          setOwnerField("mail_state", selection?.value || null);
                        }}
                        onBlur={handleOwnerBlur}
                        options={stateOptions}
                        isClearable
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-0 align-items-center" controlId="mail_code">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Postcode</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.mailCodeInput}
                        value={owner.mail_code || ""}
                        onChange={(event) => setOwnerField("mail_code", sanitizePostcode(event.target.value))}
                        onBlur={handleOwnerBlur}
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                      />
                    </Col>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6} xl={3}>
              <Card className="slm-detail-card slm-notes-card h-100">
                <Card.Header>Notes</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-0" controlId="notes">
                    <Form.Label className="visually-hidden">Notes</Form.Label>
                    <Form.Control
                      id={elementIds.notesInput}
                      value={owner.notes || ""}
                      onChange={(event) => setOwnerField("notes", event.target.value)}
                      onBlur={handleOwnerBlur}
                      as="textarea"
                      className="slm-notes-input"
                      rows={10}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mt-3" id={elementIds.contactsSection}>
            <Col xs={12}>
              <Row className="mb-2">
                <Col className="text-end">
                  <Button id={elementIds.contactsAddButton} variant="primary" size="sm" onClick={addContact} disabled={!currentOwnerId}>
                    Add New
                  </Button>
                </Col>
              </Row>
              <div className="slm-entity-contacts-table-wrap">
                <GenericTable
                  columns={ownerContactsColumns}
                  data={ownerContacts}
                  section="slm-owner-detail-contacts"
                  elementIds={elementIds.contactsTable}
                  pageSize={PAGE_SIZES.slmEntityDetail}
                  enablePagination={true}
                  hasHeaderSorting={true}
                  tableStyle={{ minWidth: "1100px" }}
                  enforceMinRowsHeight={true}
                  minVisibleRows={10}
                  meta={{
                    updateData: updateContactTableState,
                    handleSave: saveContactRecord,
                  }}
                />
              </div>
            </Col>
          </Row>

          <input type="hidden" value={currentOwnerId || ""} readOnly />
        </Form>
      )}
    </div>
  );
}

export default SLMOwnerDetail;
